// Shared DevOps agent verdicts (paolo 2026-10-03). One module-level fetch of
// GET /api/devops/findings feeds three consumers — the Dashboard status-bar
// P/S pills, the integrity dropdown "Agents" rows and the DevOps tab — so a
// signed-in session makes one request every 15 min, not one per consumer.
//
// Colour rule (paolo 2026-10-03, DevOps tab spec 2026-10-02 for the vocabulary):
//   HEALTHY, or nothing to report (no runs / findings unavailable)  → green
//   BENIGN_KNOWN, DEGRADED, and any status the UI does not recognise → amber
//     (an unfamiliar verdict word means "a human should look", never a
//     false red — red must keep meaning INCIDENT)
//   INCIDENT                                                         → red
//   fetch failed with no earlier data                                → grey
//   any status the operator has ACKNOWLEDGED (Paolo 2026-10-16)      → blue-grey
//     ("seen, handled" — the raw status stays in the text; the mark lapses
//     when the agent produces a newer verdict for that day)
// The agents own the vocabulary and may grow it; the raw status is always
// shown so a new word is visible, not hidden behind the colour.
import { ref, computed, type Ref, type ComputedRef } from 'vue'
import { api, type AgentFindings } from '../api/client'

export const REFRESH_MS = 15 * 60 * 1000

export type AgentPillTone = 'ok' | 'warn' | 'err' | 'off' | 'ack'

export interface AgentPillState {
  agent: 'platform' | 'strategy'
  label: string
  tone: AgentPillTone
  /** Status-bar pill class: sb-ok | sb-warn | sb-err | sb-off | sb-ack. */
  cls: string
  /** Raw agent status (HEALTHY, DEGRADED, …) or a synthetic one (NO RUNS, UNAVAILABLE, OFFLINE). */
  status: string
  /** True when the latest run day carries an operator acknowledgement. */
  acknowledged: boolean
  date: string | null
  module: string | null
  /** One-line summary for the dropdown row. */
  message: string
  /** Recommended action when the server sent detail (admin viewers only). */
  action: string | null
  /** Tooltip for the pill. */
  title: string
}

const AMBER = new Set(['BENIGN_KNOWN', 'DEGRADED'])

export function toneForStatus(status: string): AgentPillTone {
  const s = status.trim().toUpperCase()
  if (s === 'HEALTHY') return 'ok'
  if (s === 'INCIDENT') return 'err'
  if (AMBER.has(s)) return 'warn'
  return 'warn'
}

const LABELS: Record<'platform' | 'strategy', string> = {
  platform: 'Platform health',
  strategy: 'Strategy analysis',
}

export function pillStateFor(
  findings: AgentFindings | undefined,
  fetchFailed: boolean,
  fallback: 'platform' | 'strategy' = 'platform',
): AgentPillState {
  const agent = (findings?.agent === 'strategy' || findings?.agent === 'platform') ? findings.agent : fallback
  const label = findings?.label || LABELS[agent]
  const base = { agent, label, date: null as string | null, module: null as string | null, action: null as string | null, acknowledged: false }

  if (!findings && fetchFailed) {
    return { ...base, tone: 'off', cls: 'sb-off', status: 'OFFLINE',
      message: 'Agent findings unavailable — no usable response from the server', title: `${label}: agent findings unavailable` }
  }
  if (!findings) {
    return { ...base, tone: 'ok', cls: 'sb-ok', status: 'NO RUNS',
      message: 'No agent runs recorded yet', title: `${label}: nothing to report` }
  }
  if (!findings.available) {
    const why = findings.reason ? ` — ${findings.reason}` : ''
    return { ...base, tone: 'ok', cls: 'sb-ok', status: 'UNAVAILABLE',
      message: `Agent findings unavailable${why}`, title: `${label}: nothing to report` }
  }
  const latest = findings.days[0]
  if (!latest) {
    return { ...base, tone: 'ok', cls: 'sb-ok', status: 'NO RUNS',
      message: 'No agent runs recorded yet', title: `${label}: nothing to report` }
  }
  const acknowledged = !!latest.acknowledgement
  const tone: AgentPillTone = acknowledged ? 'ack' : toneForStatus(latest.status)
  const parts = [acknowledged ? `${latest.status} · acknowledged` : latest.status]
  if (latest.affectedModule) parts.push(latest.affectedModule)
  parts.push(latest.date)
  return {
    ...base,
    date: latest.date,
    module: latest.affectedModule,
    action: latest.detail?.recommendedAction ?? null,
    acknowledged,
    tone,
    cls: `sb-${tone}`,
    status: latest.status,
    message: parts.join(' · '),
    title: `${label}: ${latest.status}${acknowledged ? ', acknowledged' : ''} (${latest.date})`,
  }
}

interface Store {
  agents: Ref<AgentFindings[]>
  error: Ref<boolean>
  loaded: Ref<boolean>
  timer: ReturnType<typeof setInterval> | null
  starts: number
}

let store: Store | null = null

function getStore(): Store {
  if (!store) store = { agents: ref([]), error: ref(false), loaded: ref(false), timer: null, starts: 0 }
  return store
}

async function load(): Promise<void> {
  const s = getStore()
  try {
    // Defensive parse: the Playwright smokes stub every /api/** route with
    // `[]`, and a malformed body must never throw inside a render — the
    // 2026-10-03 PR #674 CI failure emptied the tab bar exactly that way.
    const r = (await api.devopsFindings()) as unknown as { agents?: unknown } | null
    const agents = Array.isArray(r?.agents) ? (r!.agents as AgentFindings[]) : null
    if (!agents) throw new Error('malformed /api/devops/findings response')
    s.agents.value = agents.filter(a => a && typeof a === 'object')
      .map(a => ({ ...a, days: Array.isArray(a.days) ? a.days : [] }))
    s.error.value = false
    s.loaded.value = true
  } catch {
    s.error.value = true
  }
}

export function useAgentFindings(): {
  agents: Ref<AgentFindings[]>
  error: Ref<boolean>
  platform: ComputedRef<AgentPillState>
  strategy: ComputedRef<AgentPillState>
  load: () => Promise<void>
  /** Returns true when this call created the poll (and fetched), false when it only joined one. */
  start: () => boolean
  stop: () => void
} {
  const s = getStore()
  const pick = (id: 'platform' | 'strategy') => computed(() =>
    pillStateFor(s.agents.value.find(a => a.agent === id), s.error.value && !s.loaded.value, id))
  return {
    agents: s.agents,
    error: s.error,
    platform: pick('platform'),
    strategy: pick('strategy'),
    load,
    // Idempotent: the first caller starts the poll, later callers only add a
    // reference. stop() releases one reference; the timer dies with the last.
    start() {
      s.starts++
      if (s.timer) return false
      void load()
      s.timer = setInterval(load, REFRESH_MS)
      return true
    },
    stop() {
      if (s.starts > 0) s.starts--
      if (s.starts === 0 && s.timer) { clearInterval(s.timer); s.timer = null }
    },
  }
}

// Test-only — tests share one module instance.
export function __resetAgentFindingsForTests() {
  if (store?.timer) clearInterval(store.timer)
  store = null
}
